import React, { useState } from 'react';

import { Moment } from 'moment';
import { useSelector } from 'react-redux';

import { useMediaQuery, useTheme } from '@material-ui/core';

import { autosaveDraft } from '../../../action/autosave/autosave-draft';
import { clearAutosavedDraft } from '../../../action/autosave/clear-autosaved-draft';
import { sendText } from '../../../contacts/actions/send-text';
import { draftMessageForEmployeeSelector } from '../../../reducers/autosave-selectors';
import { employeeContextSelector } from '../../../reducers/contact-selectors';

import { EmployeeTextMessageList } from '../../../employees/employee-text-message-list';
import { ProductTelemetryController } from '../../../product-telemetry/product-telemetry-controller';
import { EmployeeMessageComposer } from '../../message-composer/employee-message-composer';
import { EmployeeMessageTemplatesDialog } from '../../message-composer/employee-message-templates-dialog';

import { AUTOSAVED_DRAFT_TYPES } from '../../../util/autosaved-draft-types';
import { EmployeeCanMessage } from '../../../util/can-message';
import { MESSAGE_TYPE, MessageType } from '../../../util/message-types';
import { TEMPLATE_TYPE } from '../../../util/template-type';
import { useThunkDispatch } from '../../../util/use-thunk-dispatch';
import { EmployeeMessageBanners } from '../../widgets/employee-message-banners';

type InputProps = {
  orgId: number;
  employee: Employee;
  onClose: () => void;
};

export const EmployeeMessages = (props: InputProps) => {
  const { orgId, employee, onClose } = props;

  const [messageType, setMessageType] = useState<MessageType | null>(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState<boolean>(false);

  const theme = useTheme();
  const isOnMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const messageComposerRef: React.RefObject<HTMLDivElement> = React.createRef();
  const dispatch = useThunkDispatch();

  const messageDraft = useSelector((state: any) =>
    draftMessageForEmployeeSelector(state, employee.employeeId),
  );

  const handleToggleTemplateDialogVisible = () => {
    setTemplateDialogOpen(!templateDialogOpen);
  };

  // IF (07/07/20): It's possible that the message composer gains focus and then the messages finish
  // loading - this pushes the composer out of view and starts the user at the top of the message
  // thread. That's frustrating for long threads, so we scroll the composer back into view when the
  // messages load.
  const handleMessageThreadLoaded = () => {
    messageComposerRef.current?.scrollIntoView(false);
  };

  const handleMessageTypeChange = (newMessageType: MessageType) => {
    setMessageType(newMessageType);
    if (Object.values(MESSAGE_TYPE).includes(newMessageType)) {
      handleUpdateMessage({ newType: newMessageType });
    }
  };

  const handleSubjectChange = (newSubject: string) => {
    handleUpdateMessage({ newSubject });
  };

  const handleTemplateSelect = (selectedSavedMessage: string) => {
    handleUpdateMessage({ newMessage: selectedSavedMessage });
  };

  const handleSendMessage =
    (contactId: number) =>
    async (
      message: string,
      type: string,
      subject?: string | null,
      sendDateTime?: Moment | null,
      optionalFields?: {
        urlFileMappings?: Record<string, string>;
        sendWithVCard?: boolean;
      },
    ) => {
      ProductTelemetryController.logEvent('clicked send employee message', {
        value: contactId,
        metadata: {
          isOnMobile: isOnMobile ? 'true' : 'false',
          type,
          isScheduled: sendDateTime ? 'true' : 'false',
        },
      });

      const sendVCardForOrgId = optionalFields?.sendWithVCard ? orgId : undefined;
      const result = await dispatch(
        sendText(
          contactId,
          employeeContextSelector(employee.employeeId),
          message,
          type,
          subject,
          { orgId },
          sendDateTime,
          optionalFields?.urlFileMappings,
          sendVCardForOrgId,
        ),
      );

      return result;
    };

  const handleClearDraft = () => {
    if (employee.employeeId) {
      dispatch(
        clearAutosavedDraft(AUTOSAVED_DRAFT_TYPES.MESSAGE, { employeeId: employee.employeeId }),
      );
    }
  };

  const handleUpdateMessage = ({
    newSubject,
    newType,
    newMessage,
  }: {
    newSubject?: string;
    newType?: MessageType;
    newMessage?: string;
  }) => {
    // These checks prevent us from saving a blank draft
    if (newType && !messageDraft?.messageBody) {
      // When we don't have a message body
      return;
    }
    if (!messageDraft && !newSubject && !newMessage) {
      // When CKEditor fires an onChange after sending the message
      return;
    }
    if (!employee.employeeId) {
      return;
    }

    const coalescedNewMessage =
      newMessage || (newMessage === undefined ? messageDraft?.messageBody : undefined);
    const coalescedNewType = newType || (newType === undefined ? messageDraft?.messageType : '');
    const coalescedNewSubject =
      newSubject || (newSubject === undefined ? messageDraft?.subject : '');

    dispatch(
      autosaveDraft(AUTOSAVED_DRAFT_TYPES.MESSAGE, {
        context: { employeeId: employee.employeeId },
        messageBody: coalescedNewMessage || '',
        messageType: coalescedNewType || messageType || MESSAGE_TYPE.TEXT,
        subject: coalescedNewSubject,
      }),
    );
  };

  let canMessage = null;
  let enableText = false;
  let enableEmail = false;
  if (employee) {
    canMessage = new EmployeeCanMessage(employee);
    enableText = canMessage.canReceive(MESSAGE_TYPE.TEXT);
    enableEmail = canMessage.canReceive(MESSAGE_TYPE.EMAIL);
  }

  return (
    <div className="u-margin-bottom-clearChat">
      <EmployeeTextMessageList
        employeeId={employee.employeeId}
        onMessagesLoaded={handleMessageThreadLoaded}
      />
      <EmployeeMessageBanners
        canMessage={canMessage}
        employee={employee}
        messageInputHidden={!enableText && !enableEmail}
      />
      {messageDraft?.messageBody && (
        <div className="typo-subtitle u-textAlign-right u-width-100 u-margin-top">
          Draft saved {messageDraft?.savedAt.format('dddd, MMMM Do, h:mm a')}
        </div>
      )}
      <div ref={messageComposerRef} className="u-margin-top-sm">
        <EmployeeMessageComposer
          activeEmployee={employee}
          message={messageDraft?.messageBody || null}
          enableText={enableText}
          enableEmail={enableEmail}
          handleToggleTemplateDialog={handleToggleTemplateDialogVisible}
          handleTemplateSelect={handleTemplateSelect}
          handleSendMessageToContact={handleSendMessage}
          onMessageTypeChange={handleMessageTypeChange}
          orgId={orgId}
          onClose={onClose}
          initialType={messageDraft?.messageType || MESSAGE_TYPE.TEXT}
          subject={messageDraft?.subject}
          onSubjectChange={handleSubjectChange}
          clearDraft={handleClearDraft}
        />
      </div>
      {(enableText || enableEmail) && templateDialogOpen && (
        <EmployeeMessageTemplatesDialog
          employeeId={employee.employeeId}
          orgId={orgId}
          insertTemplate={handleTemplateSelect}
          templateType={TEMPLATE_TYPE.MESSAGE}
          messageType={messageType}
          onClose={handleToggleTemplateDialogVisible}
        />
      )}
    </div>
  );
};
